import { SortDirection } from 'components/Table/types';

const isEmptyValue = (value: unknown): boolean =>
  value === null || value === undefined || String(value).trim() === '';

const toNumber = (value: unknown): number | null => {
  const parsed = Number(value);
  return !isEmptyValue(value) && !Number.isNaN(parsed) ? parsed : null;
};

// Values often reach the table as strings even when they are numbers, so they
// are compared as numbers when both sides are one - otherwise "1200" would sort
// before "59". numeric also keeps "Height 2" ahead of "Height 10" in text.
const compareTableValues = (left: unknown, right: unknown): number => {
  const leftNumber = toNumber(left);
  const rightNumber = toNumber(right);
  if (leftNumber !== null && rightNumber !== null) {
    return leftNumber - rightNumber;
  }
  return String(left).localeCompare(String(right), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
};

// For tables that hold all their rows at once and have no backend sorting.
// Empty cells stay at the bottom in both directions, so sorting a mostly empty
// column still brings the rows that have a value to the top.
export const sortTableRows = <T>(
  rows: T[],
  field: keyof T | undefined,
  sortDirection: SortDirection | undefined
): T[] => {
  if (sortDirection === SortDirection.NONE || !field) {
    return rows;
  }

  const rowsWithValue = rows.filter((row: T) => !isEmptyValue(row[field]));
  const rowsWithoutValue = rows.filter((row: T) => isEmptyValue(row[field]));

  const sorted = rowsWithValue.sort((left: T, right: T) =>
    compareTableValues(left[field], right[field])
  );

  return [
    ...(sortDirection === SortDirection.DESC ? sorted.reverse() : sorted),
    ...rowsWithoutValue,
  ];
};
